<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'TaskStatus',
  });

  const componentMetadata = {
    kind: 'usercontrol',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    taskId: {
      type: String,
      required: true,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const onPoll = (...args) => {
    return $fn.value
      .fetch('read_invoice_generation_task', {
        parameters: { id: props.taskId },
      })
      .then((data) => {
        setData('item', data);
        return data;
      });
  };

  const item = ref(null);
  const pollerInstance = ref(null);
  const ux = ref([]);
  const b_8564662037462133_variantMap = computed(() => ({
    PENDING: 'warning',
    STARTED: 'primary',
    FAILURE: 'danger',
  }));
  const b_1889980785424776_options = computed(() => ({
    lineNumbers: true,
    matchBrackets: true,
    continueComments: 'Enter',
  }));

  const $data = computed(() => ({
    item: item.value,
    pollerInstance: pollerInstance.value,
    ux: ux.value,
    b_8564662037462133_variantMap: b_8564662037462133_variantMap.value,
    b_1889980785424776_options: b_1889980785424776_options.value,
  }));

  const $$load_pollerInstance = async () => {
    return $fn.value.poller({ interval: 3000, callback: onPoll }).start();
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'item' },
      { name: 'pollerInstance' },
      { name: 'ux' },
      { name: 'b_8564662037462133_variantMap', nodeId: '8564662037462133' },
      { name: 'b_1889980785424776_options', nodeId: '1889980785424776' },
    ],
    loaders: { pollerInstance: $$load_pollerInstance },
    ux: {},
    dataRefs: {
      item,
      pollerInstance,
      ux,
      b_8564662037462133_variantMap,
      b_1889980785424776_options,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  watch(
    () => props.taskId,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('taskId');
    }
  );

  const act_3343864469950044_script_ysh1j0dr5_value_callable = async (
    $event
  ) => {
    return pollerInstance.value.manualPoll();
  };
  const act_3343864469950044_script_ysh1j0dr5_getActionArgs = async (
    $event
  ) => {
    return {
      value: async () => {
        await act_3343864469950044_script_ysh1j0dr5_value_callable($event);
      },
    };
  };

  const act_7315092382398562_reloadComponentData_90aesfcl2 =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_q4rtqi2n2 =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_90aesfcl2,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_q4rtqi2n2],
    event: 'reload',
  });
  const act_3343864469950044_script_ysh1j0dr5 = new actionTypes.ScriptAction({
    actionArgs: act_3343864469950044_script_ysh1j0dr5_getActionArgs,
  });
  const inst_evh_3343864469950044_click_gkiugqc99 =
    new actionTypes.EventHandlerImpl({
      action: act_3343864469950044_script_ysh1j0dr5,
      event: 'click',
      displayName: 'script',
    });
  const inst_evh_3343864469950044_click = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_3343864469950044_click_gkiugqc99],
    event: 'click',
  });

  const actions = {
    inst_evh_7315092382398562_reload_q4rtqi2n2,
    inst_evh_7315092382398562_reload,
    inst_evh_3343864469950044_click_gkiugqc99,
    inst_evh_3343864469950044_click,
  };

  const evh_7315092382398562_reload_q4rtqi2n2 = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_q4rtqi2n2.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_3343864469950044_click_gkiugqc99 = async (event, scope) => {
    return await actions.inst_evh_3343864469950044_click_gkiugqc99.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_3343864469950044_click = async (event, scope) => {
    return await actions.inst_evh_3343864469950044_click.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpUsercontrol
    :loading="loading"
    :reloading="reloading"
    :loaded="loaded"
    :error="error"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <ItpBox class="page d-flex flex-column" v-if="item">
      <ItpBadge
        :text="item.status"
        :value="item.status"
        :variantMap="b_8564662037462133_variantMap"
      >
      </ItpBadge>
      <ItpButton text="poll" @click="evh_3343864469950044_click($event, {})">
      </ItpButton>
      <ItpCodeMirror
        :options="b_1889980785424776_options"
        v-model="item.result_text"
        :value="item.result_text"
        @input="
          !($event && typeof $event.stopPropagation === 'function') &&
          (item.result_text =
            $event && typeof $event === 'object' && 'data' in $event
              ? $event.data
              : $event)
        "
        v-if="item.result_text"
      >
      </ItpCodeMirror>
    </ItpBox>
  </ItpUsercontrol>
</template>
